import { createSourceNotice } from '../components/ui/SourceNotice';
import { ROUTES } from '../lib/routes';
import { navigate } from '../router';
import {
  computeNewInstallResult,
  autofillNotice,
  type FacilityType,
  type BuildStage,
  type FastNeed,
} from '../mock/newInstallCriteria';
import { el, clear } from '../utils/dom';

export function renderNewInstallPage(root: HTMLElement): void {
  const wrap = el('div', {});

  wrap.append(
    el('div', { className: 'ev123-page-head' }, [
      el('div', { className: 'ev123-container' }, [
        el('h1', { className: 'ev123-page-head__title', text: '신규설치 기준 계산' }),
        el('p', {
          className: 'ev123-page-head__lead',
          text: '신축·허가·설계·시공 전 단계에서 설치 검토 기준을 빠르게 정리합니다. 산출 결과는 참고용이며, 법령·조례·현장 조건을 반드시 확인해 주세요.',
        }),
      ]),
    ])
  );

  const container = el('div', { className: 'ev123-container' });

  const parkingInput = el('input', {
    className: 'ev123-input',
    type: 'number',
    min: '0',
    step: '1',
    placeholder: '숫자 입력',
    'aria-label': '총 주차면수',
  }) as HTMLInputElement;

  const facilityInput = el('input', {
    className: 'ev123-input',
    type: 'text',
    placeholder: '예) OO아파트 또는 도로명 주소',
    'aria-label': '시설명 또는 주소',
  }) as HTMLInputElement;

  const autof = el('section', { className: 'ev123-section ev123-section--tight' });
  const autofCard = el('div', { className: 'ev123-card' });
  autofCard.append(
    el('h2', { className: 'ev123-card__title', text: '주차면 정보 자동 조회' }),
    el('p', {
      className: 'ev123-card__desc',
      text: '시설명 또는 주소를 입력한 뒤 조회해 보세요. 조회 결과는 참고용이며, 최종 값은 현장 기준으로 확인·수정할 수 있습니다.',
    }),
    el('div', { className: 'ev123-field' }, [
      el('label', { className: 'ev123-label', text: '시설명 또는 주소' }),
      facilityInput,
    ]),
    el('div', { className: 'ev123-field' }, [
      el('label', { className: 'ev123-label', text: '총 주차면수' }),
      parkingInput,
      el('p', {
        className: 'ev123-field-hint',
        text: '자동 조회 후에도 주차면 수는 언제든지 직접 수정할 수 있습니다.',
      }),
    ])
  );

  const autofillOut = el('div', { className: 'ev123-stack' });
  const runAutofill = (): void => {
    const hasFacility = facilityInput.value.trim().length > 0;
    const scenario = hasFacility ? 'success' : 'notFound';
    const { variant, title, body } = autofillNotice(scenario);
    clear(autofillOut);
    autofillOut.append(createSourceNotice(variant === 'success' ? 'success' : 'danger', title, body));
    if (scenario === 'success') {
      parkingInput.value = '420';
    }
  };

  const primary = el('button', { type: 'button', className: 'ev123-btn ev123-btn--secondary' }) as HTMLButtonElement;
  primary.textContent = '주차면 정보 자동 조회';
  primary.addEventListener('click', runAutofill);
  autofCard.appendChild(primary);

  autofillOut.append(
    createSourceNotice(
      'neutral',
      '데이터 연계 안내',
      '주소 또는 시설명을 기준으로 주차면·건축 정보 확인 절차가 순차적으로 연동될 예정입니다.'
    )
  );
  autofCard.appendChild(autofillOut);
  autof.appendChild(autofCard);
  container.appendChild(autof);

  const formSec = el('section', { className: 'ev123-section ev123-section--tight' });
  const formCard = el('div', { className: 'ev123-card' });
  formCard.append(el('h2', { className: 'ev123-card__title', text: '시설 정보 입력' }));

  const facilityType = selectField<FacilityType>(
    '시설 유형',
    [
      ['housing', '공동주택'],
      ['public', '공공기관'],
      ['commercial', '상업시설'],
      ['office', '업무시설'],
      ['other', '기타'],
    ],
    'housing'
  );

  const buildStage = selectField<BuildStage>(
    '건축 단계',
    [
      ['plan', '계획 단계'],
      ['permit', '건축허가 단계'],
      ['design', '설계 단계'],
      ['preconstruction', '시공 전'],
      ['other', '기타'],
    ],
    'design'
  );

  const planned = el('input', {
    className: 'ev123-input',
    type: 'number',
    min: '0',
    step: '1',
    placeholder: '입력하지 않아도 됩니다',
  }) as HTMLInputElement;

  const fastNeed = selectField<FastNeed>(
    '급속 설치 필요 여부',
    [
      ['none', '검토 필요 없음'],
      ['review', '검토 중'],
      ['needed', '필요'],
      ['unknown', '잘 모르겠음'],
    ],
    'unknown'
  );

  formCard.append(
    facilityType.wrap,
    buildStage.wrap,
    el('div', { className: 'ev123-field' }, [
      el('label', { className: 'ev123-label', text: '현재 계획 충전기 수(선택)' }),
      planned,
      el('p', {
        className: 'ev123-field-hint',
        text: '입력 시 검토 기준과 비교해 볼 수 있습니다.',
      }),
    ]),
    fastNeed.wrap
  );

  const submit = el('button', { type: 'button', className: 'ev123-btn ev123-btn--primary' }) as HTMLButtonElement;
  submit.textContent = '설치기준 계산하기';
  formCard.appendChild(submit);

  const resultHost = el('div', { className: 'ev123-result-stack' });

  submit.addEventListener('click', () => {
    const tp = Number(parkingInput.value);
    if (!Number.isFinite(tp) || tp <= 0) {
      import('../components/toast').then((m) => m.showToast('총 주차면수를 입력해 주세요.'));
      return;
    }
    const plannedVal = planned.value === '' ? undefined : Number(planned.value);
    const res = computeNewInstallResult({
      facilityType: facilityType.get(),
      totalParking: tp,
      buildStage: buildStage.get(),
      plannedChargers: plannedVal,
      fastNeed: fastNeed.get(),
    });
    clear(resultHost);
    resultHost.append(renderResultBlocks(res, plannedVal));
    resultHost.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  formCard.appendChild(resultHost);
  formSec.appendChild(formCard);
  container.appendChild(formSec);

  wrap.appendChild(container);
  root.appendChild(wrap);
}

function selectField<T extends string>(
  label: string,
  options: [T, string][],
  initial: T
): { wrap: HTMLElement; get: () => T } {
  const wrap = el('div', { className: 'ev123-field' });
  wrap.appendChild(el('label', { className: 'ev123-label', text: label }));
  const sel = el('select', { className: 'ev123-select', 'aria-label': label }) as HTMLSelectElement;
  for (const [v, t] of options) {
    const o = el('option', { value: v, text: t }) as HTMLOptionElement;
    if (v === initial) o.selected = true;
    sel.appendChild(o);
  }
  wrap.appendChild(sel);
  return {
    wrap,
    get: () => sel.value as T,
  };
}

function renderResultBlocks(
  res: ReturnType<typeof computeNewInstallResult>,
  planned: number | undefined
): DocumentFragment {
  const frag = document.createDocumentFragment();

  const summary = el('div', { className: 'ev123-card ev123-result-block' });
  summary.append(
    el('h3', { className: 'ev123-result-block__title', text: '검토 기준 요약' }),
    el('p', { className: 'ev123-card__desc', text: res.reviewSummary })
  );
  const metrics = el('div', { className: 'ev123-metrics ev123-metrics--compact' });
  metrics.append(
    metric('검토 기준 대수', String(res.guidelineReviewUnits)),
    metric('계획 대수', planned === undefined || Number.isNaN(planned) ? '—' : String(planned))
  );
  summary.appendChild(metrics);
  frag.appendChild(summary);

  const extra = el('div', { className: 'ev123-card ev123-result-block' });
  extra.append(
    el('h3', { className: 'ev123-result-block__title', text: '추가 확인 필요 항목' }),
    el('p', { className: 'ev123-card__desc', text: res.planComparisonHint })
  );
  const list = el('ul', { className: 'ev123-checklist' });
  for (const c of res.checkpoints) {
    const li = el('li', { text: c });
    list.appendChild(li);
  }
  extra.appendChild(list);
  frag.appendChild(extra);

  const cta = el('div', { className: 'ev123-card ev123-result-block ev123-result-block--cta' });
  cta.append(
    el('h3', { className: 'ev123-result-block__title', text: '상담 연결' }),
    el('p', {
      className: 'ev123-card__desc',
      text: '현장 조건·전력·운영 방식에 맞춘 검토가 필요하시면 상담을 요청해 주세요.',
    }),
    createSourceNotice(
      'neutral',
      '참고 사항',
      '본 화면의 산출은 참고용이며, 법적·행정적 판단이나 인허가 심사를 대신하지 않습니다.'
    )
  );
  const row = el('div', { className: 'ev123-hero__actions ev123-hero__actions--tight' });
  const c1 = el('button', { type: 'button', className: 'ev123-btn ev123-btn--primary' }) as HTMLButtonElement;
  c1.textContent = '상담 신청';
  c1.addEventListener('click', () => navigate(ROUTES.consult));
  row.appendChild(c1);
  cta.appendChild(row);
  frag.appendChild(cta);

  return frag;
}

function metric(label: string, value: string): HTMLElement {
  const m = el('div', { className: 'ev123-metric' });
  m.append(
    el('div', { className: 'ev123-metric__label', text: label }),
    el('div', { className: 'ev123-metric__value', text: value })
  );
  return m;
}
